import React, { useState, useEffect, useRef, useCallback } from 'react'; 
import io from 'socket.io-client';
import './VoiceIndicator.css'; // We'll create this CSS file for the indicator style

// IMPORTANT: Use your actual backend server URL (HTTPS) and port
const SOCKET_SERVER_URL = 'https://forteindiedev.com:3081';

// Configuration for STUN servers (helps discover public IP address)
// Using Google's public STUN servers
const peerConnectionConfig = {
    iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
        // You might add TURN servers here later if needed for difficult networks
    ]
};

const VAD_THRESHOLD = 5; // Sensitivity threshold (0-100), adjust as needed
const VAD_SMOOTHING_TIME = 0.1; // AnalyserNode smoothing

function VoiceChatPage() {
    // State variables
    const [socket, setSocket] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [roomId, setRoomId] = useState('general-voice'); // Default room ID, can be changed
    const [isMuted, setIsMuted] = useState(false)
    const [micPermission, setMicPermission] = useState('prompt'); // 'prompt', 'granted', 'denied'
    const [isJoining, setIsJoining] = useState(false); // Tracks joining process
    const [isInRoom, setIsInRoom] = useState(false); // Tracks if successfully joined

    // Refs for stable objects/values that don't need to trigger re-renders on change
    const localStreamRef = useRef(null); // Holds the local MediaStream
    const peerConnectionsRef = useRef({}); // Holds RTCPeerConnection objects { peerId: pc }
    const localAudioRef = useRef(); // Ref for the local audio element

    const [isLocalSpeaking, setIsLocalSpeaking] = useState(false);
    const [remoteSpeakingStatus, setRemoteSpeakingStatus] = useState({}); // { peerId: boolean }
    const audioAnalysisRefs = useRef({}); // { streamId: { analyser, dataArray, source, animationFrameId, audioContext } }


    // State for remote streams { peerId: MediaStream }
    const [remoteStreams, setRemoteStreams] = useState({});

    // --- Effect 1: Establish Socket.IO connection ---
    useEffect(() => {
        console.log('Attempting to connect to socket server...');
        // Initialize socket connection
        const newSocket = io(SOCKET_SERVER_URL, {
            // Consider adding transports: ['websocket'] if you encounter polling issues
            // transports: ['websocket'],
            // Use secure connection since server uses HTTPS
            secure: true,
        });
        setSocket(newSocket);

        // --- Socket Event Listeners ---
        newSocket.on('connect', () => {
            console.log('Socket connected:', newSocket.id);
            setIsConnected(true);
        });

        newSocket.on('disconnect', (reason) => {
            console.log('Socket disconnected:', reason);
            setIsConnected(false);
            setSocket(null); // Clear socket state
            // Reset related state if needed
            cleanupConnections();
            localStreamRef.current?.getTracks().forEach(track => track.stop()); // Stop mic access
            localStreamRef.current = null;
            setMicPermission('prompt');
            setIsJoining(false);
            setIsInRoom(false);
        });

        newSocket.on('connect_error', (err) => {
            console.error('Socket connection error:', err);
            setIsConnected(false);
            alert(`Failed to connect to the voice chat server: ${err.message}`);
        });

        // Fired when YOU successfully join a room
        newSocket.on('existing-users', (userIds) => {
            console.log('Received existing users list:', userIds);
            setIsJoining(false); // Finished joining attempt
            setIsInRoom(true);   // Now officially in the room
            // Initiate connection TO each existing user
            userIds.forEach(userId => {
                createPeerConnection(userId, true, newSocket); // true = initiator
            });
        });

        // Fired when a NEW user joins the room YOU are already in
        newSocket.on('user-joined', (userId) => {
            console.log('New user joined:', userId);
            // Initiate connection TO the new user
            //createPeerConnection(userId, true, newSocket); // true = initiator
        });

        // Fired when receiving an OFFER from a peer
        newSocket.on('offer', async (payload) => {
            const { signal, callerId } = payload;
            console.log(`Received offer from ${callerId}`);
            const pc = createPeerConnection(callerId, false, newSocket); // false = not initiator

            try {
                // Set the remote description (the offer)
                await pc.setRemoteDescription(new RTCSessionDescription(signal));
                console.log(`Offer from ${callerId} set as remote description.`);

                // Create an answer
                const answer = await pc.createAnswer();
                console.log(`Created answer for ${callerId}.`);

                // Set the answer as local description
                await pc.setLocalDescription(answer);
                console.log(`Answer for ${callerId} set as local description.`);

                // Send the answer back to the caller
                console.log(`Sending answer to ${callerId}`);
                newSocket.emit('answer', { target: callerId, signal: answer });
            } catch (error) {
                console.error(`Error handling offer from ${callerId}:`, error);
            }
        });

        // Fired when receiving an ANSWER from a peer you sent an offer to
        newSocket.on('answer', async (payload) => {
            const { signal, calleeId } = payload;
            console.log(`Received answer from ${calleeId}`);
            const pc = peerConnectionsRef.current[calleeId];
            if (pc) {
                try {
                    // Set the remote description (the answer)
                    await pc.setRemoteDescription(new RTCSessionDescription(signal));
                    console.log(`Answer from ${calleeId} set as remote description.`);
                    // Connection should now establish
                } catch (error) {
                    console.error(`Error setting remote description for answer from ${calleeId}:`, error);
                }
            } else {
                console.warn(`Received answer from unknown peer: ${calleeId}`);
            }
        });

        // Fired when receiving an ICE candidate from a peer
        newSocket.on('ice-candidate', (payload) => {
            const { candidate, senderId } = payload;
            // console.log(`Received ICE candidate from ${senderId}`); // Can be very verbose
            const pc = peerConnectionsRef.current[senderId];
            if (pc && candidate) {
                pc.addIceCandidate(new RTCIceCandidate(candidate))
                  .catch(e => console.error(`Error adding received ICE candidate from ${senderId}:`, e));
            } else if (!pc) {
                 console.warn(`Received ICE candidate from unknown peer: ${senderId}`);
            }
        });

         // Fired when a user leaves the room
         newSocket.on('user-left', (userId) => {
            console.log('User left:', userId);
            closePeerConnection(userId);
        });

        // Cleanup on component unmount
        return () => {
            console.log('Component unmounting, disconnecting socket...');
            cleanupConnections(); // Clean up WebRTC connections
            localStreamRef.current?.getTracks().forEach(track => track.stop()); // Stop mic
            newSocket.disconnect();
        };
    }, []); // Empty dependency array ensures this effect runs only once on mount

    // // --- Effect 2: Attach local stream to audio element ---
    // useEffect(() => {
    //     if (localStream && localAudioRef.current) {
    //         console.log('Attaching local media stream to audio element.');
    //         localAudioRef.current.srcObject = localStream;
    //     }
    // }, [localStream]); // Run this effect when localStream changes

    // --- Effect 2: Attach local stream to audio element ---
    useEffect(() => {
        if (localStreamRef.current && localAudioRef.current) {
            console.log('Attaching local media stream to audio element.');
            localAudioRef.current.srcObject = localStreamRef.current;
        }
    }, [micPermission]); // Re-run if mic permission changes (e.g., stream becomes available)

    // --- NEW: Voice Activity Detection Logic ---
    const setupVoiceActivityDetector = useCallback((stream, setSpeaking) => {
        if (!stream || !stream.getAudioTracks().length) return null;

        const audioContext = new AudioContext();
        const analyser = audioContext.createAnalyser();
        analyser.smoothingTimeConstant = VAD_SMOOTHING_TIME;
        analyser.fftSize = 512;

        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);

        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        let animationFrameId;

        const checkVolume = () => {
            analyser.getByteFrequencyData(dataArray);
            const sum = dataArray.reduce((acc, val) => acc + val, 0);
            const average = sum / dataArray.length;
            const isSpeaking = average > VAD_THRESHOLD;
            setSpeaking(isSpeaking); // Update state
            animationFrameId = requestAnimationFrame(checkVolume);
        };

        animationFrameId = requestAnimationFrame(checkVolume);

        // Return cleanup function and necessary objects
        return {
            cleanup: () => {
                cancelAnimationFrame(animationFrameId);
                source.disconnect();
                analyser.disconnect();
                audioContext.close();
            },
            audioContext,
            source,
            analyser,
            animationFrameId
        };
    }, []);

    // Effect to set up voice activity for the LOCAL stream
    useEffect(() => {
        if (localStreamRef.current && !audioAnalysisRefs.current['local']) {
            console.log('Setting up local voice activity detector...');
            const vad = setupVoiceActivityDetector(localStreamRef.current, setIsLocalSpeaking);
            if (vad) {
                audioAnalysisRefs.current['local'] = vad;
            }
        }
        // Cleanup local VAD when stream is gone (on disconnect/unmount)
        return () => {
            if (audioAnalysisRefs.current['local']) {
                console.log('Cleaning up local voice activity detector.');
                audioAnalysisRefs.current['local'].cleanup();
                delete audioAnalysisRefs.current['local'];
            }
        };
    }, [localStreamRef.current, setupVoiceActivityDetector]);


    // Effect to set up/tear down voice activity for REMOTE streams
    useEffect(() => {
        const currentPeerIds = Object.keys(remoteStreams);
        const analyzedPeerIds = Object.keys(audioAnalysisRefs.current).filter(id => id !== 'local');

        // Set up VAD for new peers
        currentPeerIds.forEach(peerId => {
            if (!analyzedPeerIds.includes(peerId) && remoteStreams[peerId]) {
                console.log(`Setting up remote voice activity detector for ${peerId}`);
                const vad = setupVoiceActivityDetector(remoteStreams[peerId], (isSpeaking) => {
                    setRemoteSpeakingStatus(prev => ({ ...prev, [peerId]: isSpeaking }));
                });
                if (vad) {
                    audioAnalysisRefs.current[peerId] = vad;
                }
            }
        });

        // Clean up VAD for disconnected peers
        analyzedPeerIds.forEach(peerId => {
            if (!currentPeerIds.includes(peerId)) {
                console.log(`Cleaning up remote voice activity detector for ${peerId}`);
                if (audioAnalysisRefs.current[peerId]) {
                    audioAnalysisRefs.current[peerId].cleanup();
                    delete audioAnalysisRefs.current[peerId];
                }
                setRemoteSpeakingStatus(prev => {
                    const newStatus = {...prev};
                    delete newStatus[peerId];
                    return newStatus;
                });
            }
        });

    }, [remoteStreams, setupVoiceActivityDetector]);

    // --- WebRTC Helper Functions ---

     // Function to create and configure a peer connection
     const createPeerConnection = useCallback((peerId, isInitiator, currentSocket) => {
        if (!localStreamRef.current) {
            console.error("Cannot create peer connection without local stream.");
            return null;
        }
         if (!currentSocket) {
             console.error("Socket not available for creating peer connection.");
             return null;
         }
         // Close existing connection for this peer if any (safety measure)
         if (peerConnectionsRef.current[peerId]) {
             console.warn(`Closing existing connection for peer: ${peerId}`);
             closePeerConnection(peerId);
         }

         console.log(`Creating peer connection for ${peerId}, Initiator: ${isInitiator}`);
         const pc = new RTCPeerConnection(peerConnectionConfig);

         // Store the connection
         peerConnectionsRef.current[peerId] = pc;

         // Add local audio track to the connection
         localStreamRef.current.getTracks().forEach(track => {
             try {
                 pc.addTrack(track, localStreamRef.current);
                 console.log(`Added local track to connection with ${peerId}`);
             } catch (error) {
                  console.error(`Error adding track for peer ${peerId}:`, error);
             }
         });

         // --- Event Handlers for the Peer Connection ---

         // Fired when an ICE candidate needs to be sent to the peer
         pc.onicecandidate = (event) => {
             if (event.candidate) {
                 // console.log(`Sending ICE candidate to ${peerId}`); // Verbose
                 currentSocket.emit('ice-candidate', {
                     target: peerId,
                     candidate: event.candidate,
                 });
             }
         };

         // Fired when a remote track is received
         pc.ontrack = (event) => {
             console.log(`Received remote track from ${peerId}`, event.streams);
             if (event.streams && event.streams[0]) {
                 setRemoteStreams(prev => ({
                     ...prev,
                     [peerId]: event.streams[0], // Store the remote stream
                 }));
             } else {
                  // Handle cases where track might be added without a stream
                  // (less common for simple audio)
             }
         };

         // Optional: Monitor connection state
         pc.onconnectionstatechange = (event) => {
             console.log(`Connection state with ${peerId}: ${pc.connectionState}`);
             if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected' || pc.connectionState === 'closed') {
                 // Optionally try to restart ICE or close the connection
                 // closePeerConnection(peerId);
             }
         };

         // If this client is the initiator, create and send the offer
         if (isInitiator) {
             console.log(`Creating offer for ${peerId}`);
             pc.createOffer()
                 .then(offer => pc.setLocalDescription(offer))
                 .then(() => {
                     console.log(`Offer for ${peerId} set as local description. Sending...`);
                     currentSocket.emit('offer', {
                         target: peerId,
                         signal: pc.localDescription,
                     });
                 })
                 .catch(e => console.error(`Error creating offer for ${peerId}:`, e));
         }

         return pc;
     }, []); // Dependencies: None, relies on refs and args

     // Function to close a specific peer connection
     const closePeerConnection = useCallback((peerId) => {
        const pc = peerConnectionsRef.current[peerId];
        if (pc) {
            console.log(`Closing connection with ${peerId}`);
            pc.close();
            delete peerConnectionsRef.current[peerId]; // Remove from ref
            setRemoteStreams(prev => { // Remove remote stream
                const newStreams = { ...prev };
                delete newStreams[peerId];
                return newStreams;
            });
        }
     }, []);

     // Function to clean up all connections
     const cleanupConnections = useCallback(() => {
        console.log("Cleaning up all peer connections...");
        Object.keys(peerConnectionsRef.current).forEach(peerId => {
            closePeerConnection(peerId);
        });
    }, [closePeerConnection]);

    // --- Function to Request Microphone Access ---
    const getMicrophone = async () => {
        if (micPermission === 'granted' || micPermission === 'denied') {
            console.log(`Mic permission already ${micPermission}.`);
            return;
        }
        console.log('Requesting microphone access...');
        setMicPermission('prompt'); // Reset in case it was denied previously and user tries again
        try {
            // Request only audio
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
            console.log('Microphone access granted.');
            localStreamRef.current = stream; // Set the ref instead
            setMicPermission('granted');
        } catch (err) {
            console.error('Error accessing microphone:', err);
            setMicPermission('denied');
            // Provide user feedback based on the error type
            if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                alert('Microphone permission was denied. You may need to allow access in your browser settings.');
            } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
                 alert('No microphone device found. Please ensure a microphone is connected and enabled.');
            } else {
                alert(`An error occurred while accessing the microphone: ${err.message}`);
            }
        }
    };

    // --- Function to Join the Voice Chat Room ---
    const handleJoinRoom = () => {
        // Check prerequisites
        if (micPermission !== 'granted' || !localStreamRef.current) { // Check ref
            alert('Please allow microphone access first.');
            return;
        }

        if (!socket || !isConnected) {
            alert('Not connected to the signaling server.');
            return;
        }
         if (!roomId) {
            alert('Please enter a Room ID.');
            return;
        }
        if (isJoining) {
            console.log("Already attempting to join...");
            return;
        }

        console.log(`Attempting to join room: ${roomId}`);
        setIsJoining(true); // Set flag to prevent double clicks
        socket.emit('join-room', roomId);

        // Note: We'll get the 'existing-users' event back from the server
        // In Step 3, we will initiate WebRTC connections based on that event.
        console.log(`"join-room" event emitted for room: ${roomId}`);
        // We could disable the join button here or change its text
    };

    const toggleMute = () => {
        if (!localStreamRef.current) {
            console.warn("Cannot toggle mute: Local stream not available.");
            return; // Exit if no stream
        }
    
        const audioTracks = localStreamRef.current.getAudioTracks();
        if (audioTracks.length === 0) {
            console.warn("Cannot toggle mute: No audio tracks found.");
            return; // Exit if no audio tracks
        }
    
        // Calculate the NEW desired enabled state (opposite of current isMuted)
        const shouldEnable = isMuted; // If currently muted, we should enable
    
        // Apply the change to all audio tracks
        audioTracks.forEach(track => {
            track.enabled = shouldEnable;
        });
    
        // Update the component's state
        setIsMuted(!shouldEnable); // Set state to the opposite of enabled state
        console.log(`Local audio tracks ${shouldEnable ? 'enabled (unmuted)' : 'disabled (muted)'}`);
    };

    // --- Render UI ---
    return (
        <div style={{ padding: '20px' }}>
            <h2>Voice Chat Room: {isInRoom ? roomId : ''}</h2>
            <p><strong>Status:</strong> {isConnected ? `Connected (My ID: ${socket?.id})` : 'Disconnected'}</p>
            <p><strong>Mic Permission:</strong> {micPermission}</p>

            <hr />

            {/* Step 1: Get Mic Permission */}
            {!localStreamRef.current && micPermission !== 'granted' && ( /* Check ref */
                 <div>
                    <p>Please allow microphone access to join the chat.</p>
                     <button onClick={getMicrophone} disabled={micPermission === 'denied' || isJoining}>
                        {micPermission === 'prompt' ? 'Allow Microphone' : 'Permission Denied (Check Settings)'}
                    </button>
                </div>
             )}


            {/* Step 2: Join Room (only show if mic is granted and not already in room) */}
            {micPermission === 'granted' && !isInRoom && (
                <div>
                    <label htmlFor="roomIdInput">Room ID: </label>
                    <input
                        id="roomIdInput"
                        type="text"
                        value={roomId}
                        onChange={(e) => setRoomId(e.target.value)}
                        style={{ marginRight: '10px' }}
                        disabled={isJoining}
                    />
                    <button onClick={handleJoinRoom} disabled={!roomId || isJoining || !isConnected}>
                        {isJoining ? 'Joining...' : 'Join Voice Room'}
                    </button>
                </div>
            )}

             {/* --- Room Status & Controls Area --- */}
            {isInRoom && (
                <div style={{ marginTop: '10px', marginBottom: '10px' }}> {/* Added margin */}
                    <p style={{ display: 'inline-block', marginRight: '15px' }}> {/* Adjusted styling */}
                        <strong>In Room: {roomId}</strong>
                    </p>
                    {/* --- ADD MUTE BUTTON HERE --- */}
                    <button
                         onClick={toggleMute}
                         disabled={!localStreamRef.current || !localStreamRef.current.getAudioTracks().length} // Check ref directly
                     >
                         {isMuted ? 'Unmute Mic' : 'Mute Mic'}
                     </button>
                    {/* --- END OF ADDED BUTTON --- */}
                </div>
            )}

            {/* {isJoining && micPermission === 'granted' && (
                <p>Joined Room: {roomId}. Waiting for others...</p>
            )} */}


            <hr />

            {/* Local Audio Preview (Muted) */}
            {localStreamRef.current && ( /* Check ref */
                <div className="user-view">
                    <div className={`voice-indicator ${isLocalSpeaking && !isMuted ? 'speaking' : ''}`}></div>
                    <h3>My Audio (You) {isMuted ? '(Muted)' : ''}</h3>
                    <audio ref={localAudioRef} autoPlay muted playsInline />
                </div>
            )}

            <h3>Other Participants</h3>
            <div id="remote-audio-container">
                 {Object.keys(remoteStreams).length === 0 && isInRoom && <p>Waiting for others to connect...</p>}
                 {Object.entries(remoteStreams).map(([peerId, stream]) => (
                    <div key={peerId} className="user-view">
                        <div className={`voice-indicator ${remoteSpeakingStatus[peerId] ? 'speaking' : ''}`}></div>
                        <div className="user-details">
                            <p style={{ margin: '0 0 5px 0', fontSize: '0.9em' }}>User: {peerId}</p>
                            <audio
                                autoPlay
                                playsInline
                                ref={(audioElement) => {
                                    if (audioElement && audioElement.srcObject !== stream) {
                                        audioElement.srcObject = stream;
                                    }
                                }}
                            />
                        </div>
                    </div>
                 ))}
            </div>
        </div>
    );
}

export default VoiceChatPage;